import { getWalletName } from '@ankr.com/provider';
import { Logout } from '@ankr.com/ui';
import { PopoverProps } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

import { Copy, cropString, getWalletIcon } from 'modules/common';
import { useTranslation } from 'modules/i18n';
import { useAppSelector, useDisconnectMutation } from 'modules/store';

import translation from './disconnectDropdown.translation';

interface Props extends PopoverProps {
  address: string;
  className?: string;
}

export function DisconnectDropdown({
  address,
  className,
  ...rest
}: Props): JSX.Element {
  const [disconnect] = useDisconnectMutation();
  const { classes, cx } = useStyles();
  const { t, keys } = useTranslation(translation);
  const walletId = useAppSelector(store => store.auth.walletId);
  const walletIcon = walletId && getWalletIcon(walletId);

  return (
    <Popover
      data-tid="layout-header-disconnect-dropdown"
      classes={{ paper: cx(classes.root, className) }}
      {...rest}
    >
      <div className={classes.left}>
        {walletIcon && (
          <Box component="img" src={walletIcon} alt="" width={40} />
        )}
        <div className={classes.text}>
          <Typography variant="subtitle2">
            {walletId ? getWalletName(walletId) : t(keys.unknownWallet)}
          </Typography>
          <Copy variant="button3" className={classes.address} text={address}>
            {cropString(address)}
          </Copy>
        </div>
      </div>

      <Button
        data-tid="layout-header-disconnect-dropdown-button"
        variant="text"
        color="secondary"
        className={classes.button}
        endIcon={<Logout />}
        onClick={() => disconnect()}
      >
        {t(keys.disconnect)}
      </Button>
    </Popover>
  );
}

const useStyles = makeStyles()(theme => ({
  root: {
    padding: theme.spacing(5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(11),
  },
  left: {
    display: 'flex',
    alignItems: 'center',
  },
  text: {
    marginLeft: theme.spacing(2),
    display: 'flex',
    flexFlow: 'column nowrap',
  },
  address: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.grey[600],
    gap: theme.spacing(1),

    '& > svg': {
      color: theme.palette.grey[600],
      cursor: 'pointer',
    },
  },
  button: {
    '& svg': {
      color: theme.palette.grey[600],
    },
  },
}));
